import { useDispatch, useSelector } from 'react-redux';
import data from "../../db.json"; // Default fallback data source
import FileSystemService from '../../../services/FileSystemService';
import { loadAppropriateData, setCurrentManifest } from '../../../Redux/Mode/action';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { debouncedRequest } from '../../../utils/apiHelper';

/**
 * Filter movies by tag
 * @param {Array} movies - Array of movie objects to filter
 * @param {string|null} tag - Tag to filter by, returns all movies if null/undefined
 * @returns {Array} Filtered array of movies
 */
export const filterByTag = (movies, tag) => {
  if (!movies || !Array.isArray(movies)) return [];
  
  // Guard clause: return full movie list when no tag is provided - this allows hooks to get unfiltered movies
  if (!tag) return movies;
  
  // Store the original tag for logging
  const originalTag = tag;
  
  // Normalize tag for comparison (both basic normalization and tag name normalization)
  const normalizedSearchTag = tag.toLowerCase().trim();
  const standardizedSearchTag = normalizeTagName(tag);
  
  // Get the main keyword from the tag to do more flexible matching
  // For example, from "medical mythology and nano tech" we extract "medical mythology"
  const mainKeyword = normalizedSearchTag.split(' and ')[0];
  
  const result = movies.filter(movie => {
    // Make sure movie has tags and they are an array
    if (!movie.tags || !Array.isArray(movie.tags)) return false;
    
    // Check if any tag contains our search term (partial matching)
    return movie.tags.some(movieTag => {
      // Handle both string tags and object tags with name property
      const tagValue = typeof movieTag === 'string' ? movieTag : movieTag.name;
      
      if (!tagValue) return false;
      
      const normalizedMovieTag = tagValue.toLowerCase().trim();
      const standardizedMovieTag = normalizeTagName(tagValue);
      
      // Try multiple matching strategies:
      // 1. Exact match (both normalized and standardized)
      if (normalizedMovieTag === normalizedSearchTag) return true;
      if (standardizedMovieTag === standardizedSearchTag) return true;
      
      // 2. Movie tag contains our search tag
      if (normalizedMovieTag.includes(normalizedSearchTag)) return true;
      
      // 3. Our search tag contains the movie tag
      if (normalizedSearchTag.includes(normalizedMovieTag)) return true;
      
      // 4. Match against main keyword
      if (normalizedMovieTag.includes(mainKeyword) || mainKeyword.includes(normalizedMovieTag)) return true;
      
      return false;
    });
  });
  
  return result;
};

// Add these helper functions at the top, after imports
const getDurationInMinutes = (movie) => {
    const duration = movie.duration || movie.length || 0;
    return typeof duration === 'number' ? duration / 60 : 0;
};

const getDurationCategory = (movie) => {
    const minutes = getDurationInMinutes(movie);
    if (minutes >= 15) return 'film';
    if (minutes >= 5) return 'short';
    return 'clip';
};

// Improved duration filtering that handles different duration formats
export const filterByDuration = (movies, type) => {
    if (!movies || !Array.isArray(movies)) return [];
    
    return movies.filter(movie => {
        const duration = movie.duration || movie.content?.duration || 0;
        let minutes = 0;
        
        if (typeof duration === 'number') {
            // Duration already in seconds, convert to minutes
            minutes = duration / 60;
        } else if (typeof duration === 'string' && duration.includes(':')) {
            // Parse HH:MM:SS or MM:SS format
            const parts = duration.split(':').map(p => parseInt(p, 10));
            if (parts.length === 3) {
                // HH:MM:SS
                minutes = parts[0] * 60 + parts[1] + parts[2] / 60;
            } else if (parts.length === 2) {
                // MM:SS
                minutes = parts[0] + parts[1] / 60;
            }
        } else {
            // Try to parse as plain number (seconds)
            minutes = parseFloat(duration) / 60;
        }
        
        // Apply duration type filter
        switch(type) {
            case 'films':
                return minutes >= 15; // 15+ minutes
            case 'shorts':
                return minutes >= 5 && minutes < 15; // 5-15 minutes
            case 'clips':
                return minutes < 5; // Under 5 minutes
            default:
                return true; // No filtering
        }
    });
};

// Create a function to get categories that works with hooks
// Helper function to normalize tag names (merge similar tags)
const normalizeTagName = (tag) => {
  const normalized = tag.toLowerCase().trim();
  
  // Map variations to standard names
  if (normalized === 'god given rights v law') {
    return 'god given rights law';
  }
  
  return normalized;
};

// Helper function to get all unique tags from movies array
export const getUniqueTags = (movies) => {
  if (!movies || !Array.isArray(movies)) return [];
  
  // Collect all tags from all movies
  const tagSet = new Set();
  movies.forEach(movie => {
    if (movie.tags && Array.isArray(movie.tags)) {
      movie.tags.forEach(tag => {
        // Handle both string tags and object tags with name property
        const tagValue = typeof tag === 'string' ? tag : tag.name;
        if (tagValue) {
          // Normalize the tag before adding to set
          const normalizedTag = normalizeTagName(tagValue);
          tagSet.add(normalizedTag);
        }
      });
    }
  });
  
  // Convert Set to Array and sort alphabetically
  return Array.from(tagSet).sort();
};

// Helper function to get unique tags with video counts
// Recently Added — newest first by dateAdded/content.dateAdded (owner 9-7-26)
export const sortByNewest = (movies) => {
  if (!movies || !Array.isArray(movies)) return [];
  return [...movies]
    .filter(Boolean)
    .sort((a, b) => {
      const da = new Date(a.dateAdded || a.content?.dateAdded || 0).getTime();
      const db = new Date(b.dateAdded || b.content?.dateAdded || 0).getTime();
      return db - da;
    });
};

export const getUniqueTagsWithVideos = (movies) => {
  if (!movies || !Array.isArray(movies)) return [];
  
  // Use an object to count videos per tag
  const tagCount = {};
  
  movies.forEach(movie => {
    if (movie.tags && Array.isArray(movie.tags)) {
      movie.tags.forEach(tag => {
        // Handle both string tags and object tags with name property
        const tagValue = typeof tag === 'string' ? tag : tag.name;
        if (tagValue) {
          // Normalize the tag before counting
          const normalizedTag = normalizeTagName(tagValue);
          tagCount[normalizedTag] = (tagCount[normalizedTag] || 0) + 1;
        }
      });
    }
  });
  
  // Convert to array of objects with tag and count
  return Object.entries(tagCount)
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => a.tag.localeCompare(b.tag));
};

export const useCategoriesData = () => {
  const dispatch = useDispatch();
  // Get movies array from Redux state
  const moviesData = useSelector(state => {
    return state.Mode?.dataSource?.movies || [];
  });
  const location = useLocation(); // Get current route path
  
  // Determine current view mode based on route path
  const currentViewMode = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes('films')) return 'films';
    if (path.includes('shorts')) return 'shorts';
    if (path.includes('clips')) return 'clips';
    return 'all'; // Default/home view
  }, [location.pathname]);
  
  // Track if the component is mounted
  const isMounted = useRef(true);
  // Which manifest is already loaded in Redux (kills redundant reloads — Polish Run 1 §4)
  const currentManifest = useSelector(state => state.Mode?.currentManifest);
  const currentManifestRef = useRef(currentManifest);
  currentManifestRef.current = currentManifest;
  const skipLoadRef = useRef(false);
  
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // Load data when component mounts or mode changes
  useEffect(() => {
    console.log("Data component loading data, view mode:", currentViewMode);
    skipLoadRef.current = false; // Re-evaluate on every mode change
    
    // Load appropriate manifest based on view mode
      // Use debounced request to avoid multiple identical requests
      const manifestCacheKey = `manifests-${currentViewMode}`;
      
      debouncedRequest(manifestCacheKey, () => {
        return FileSystemService.getManifests();
      }).then(manifestsResult => {
        if (!isMounted.current) return; // Don't update state if component is unmounted
        
        if (manifestsResult.success && manifestsResult.manifests?.length > 0) {
          let viewModeManifests = [];
          
          // Find manifests matching the current view mode
          if (currentViewMode === 'films') {
            viewModeManifests = manifestsResult.manifests.filter(name => 
              name.includes('-films-manifest-'));
          } else if (currentViewMode === 'shorts') {
            viewModeManifests = manifestsResult.manifests.filter(name =>
              name.includes('-shorts-manifest-'));
          } else if (currentViewMode === 'clips') {
            viewModeManifests = manifestsResult.manifests.filter(name =>
              name.includes('-clips-manifest-'));
          } else {
            // For home/default view, use the standard manifest
            viewModeManifests = manifestsResult.manifests.filter(name =>
              name.startsWith('burrow-stream-manifest-') && 
              !name.includes('-films-') && 
              !name.includes('-shorts-') && 
              !name.includes('-clips-'));
          }
            
          if (viewModeManifests.length > 0) {
            // Sort by date and use most recent
            const sortedManifests = [...viewModeManifests].sort().reverse();
            const latestManifest = sortedManifests[0];

            if (currentManifestRef.current === latestManifest) {
              // Manifest for this mode is ALREADY loaded — skip the reload so the page
              // renders instantly from Redux (Polish Run 1 §4: no skeleton flash).
              console.log(`[Data] ${latestManifest} already current — skipping reload`);
              skipLoadRef.current = true;
              return;
            }

            console.log(`Loading ${currentViewMode === 'all' ? 'main' : currentViewMode} manifest: ${latestManifest}`);
            
            // Debounce the manifest loading to prevent multiple identical requests
            dispatch(setCurrentManifest(latestManifest));
          } else {
            // No manifest exists for this mode — content comes from the shared pool.
            skipLoadRef.current = true;
          }
        }
      }).catch(error => {
        console.error("Error fetching manifests:", error);
      });
    
    // Debounce the data loading to prevent multiple identical requests
    const loadDataTimeout = setTimeout(() => {
      // Dispatch only when this mode actually needs a load — a redundant dispatch flips
      // Mode.loading true and flashes skeletons over content that's already ready.
      if (isMounted.current && !skipLoadRef.current) {
        dispatch(loadAppropriateData());
      }
    }, 50); // Small delay to allow manifest setting to complete
    
    return () => {
      clearTimeout(loadDataTimeout);
    };
  }, [dispatch, currentViewMode]);
  
  // Use the data directly from redux, with fallback
  const movies = useMemo(() => {
    if (Array.isArray(moviesData) && moviesData.length > 0) {
      return moviesData;
    }
    // Use fallback data from db.json
    return data.movies || [];
  }, [moviesData]);
  
  // Generate categories from movies data
  const categories = useMemo(() => {
    return {
      // Standard tag-based categories
      homeLanderRow: filterByTag(movies, "home lander row"),
      test: filterByTag(movies, "test"),
      burrowPicks: filterByTag(movies, "burrow picks"),
      burrowUpdates: filterByTag(movies, "burrow updates"),
      disclosureHubFilms: filterByTag(movies, "disclosure hub films"),
      secretCivics: filterByTag(movies, "secret civics"),
      occultKattClips: filterByTag(movies, "occult katt clips"),
      daringDoctors: filterByTag(movies, "daring doctors"),
      injectionJustice: filterByTag(movies, "injection justice"),
      higherRealities: filterByTag(movies, "higher realities"),
      microwaveTechnology: filterByTag(movies, "microwave tech"),
      medicalMythologyAndNanoTech: filterByTag(movies, "medical mythology"),
      healthWealth: filterByTag(movies, "health wealth"),
      godGivenRightsLaw: filterByTag(movies, "god given rights law"),
      godGivenRightsVLaw: filterByTag(movies, "god given rights v law"),
      bigTechTakeOver: filterByTag(movies, "big tech take over"),
      publicDisplays: filterByTag(movies, "public displays"),
      relationshipWars: filterByTag(movies, "relationship wars"),
      cloningAndGeneticEngineering: filterByTag(movies, "genetic engineering"),
      cryptidsAndUFOs: filterByTag(movies, "cryptids and ufos"),
      generalConspiracy: filterByTag(movies, "general conspiracy"),
      freeEnergyPotential: filterByTag(movies, "free energy"),
      falseIdolsAndTrauma: filterByTag(movies, "false idol trauma"),
      darkEliteOccult: filterByTag(movies, "dark elite occult"),
      agenda2030WEFNWOAndFEMA: filterByTag(movies, "wef nwo and fema"),
      the911Archive: filterByTag(movies, "the 911 archive"),
      qAndTeamNonBiasArchive: filterByTag(movies, "q team archive"),
      weatherModification: filterByTag(movies, "weather modification"),
      zombiesDatabase: filterByTag(movies, "zombies database"),
      
      // Duration-specific categories
      allFilms: filterByDuration(movies, 'films'),
      allShorts: filterByDuration(movies, 'shorts'),
      allClips: filterByDuration(movies, 'clips'),
      
      // Combined tag-duration categories
      burrowPicksFilms: filterByDuration(filterByTag(movies, "burrow picks"), 'films'),
      burrowPicksShorts: filterByDuration(filterByTag(movies, "burrow picks"), 'shorts'),
      burrowPicksClips: filterByDuration(filterByTag(movies, "burrow picks"), 'clips'),
      
      disclosureHubFilmsLong: filterByDuration(filterByTag(movies, "disclosure hub films"), 'films'),
      disclosureHubFilmsShorts: filterByDuration(filterByTag(movies, "disclosure hub films"), 'shorts'),
      disclosureHubFilmsClips: filterByDuration(filterByTag(movies, "disclosure hub films"), 'clips'),
    };
  }, [movies]);
  
  return categories;
};

/**
 * Hook to get movies filtered by a specific tag
 * @param {string|null} tag - Tag to filter by, returns all movies if null/undefined
 * @returns {Array} Filtered array of movies
 */
export const useFilteredMovies = (tag) => {
  const moviesData = useSelector(state => state.Mode?.dataSource?.movies);
  
  // Use fallback data if needed
  const movies = useMemo(() => {
    if (Array.isArray(moviesData) && moviesData.length > 0) {
      return moviesData;
    }
    return data.movies || [];
  }, [moviesData]);
  
  const filtered = useMemo(() => filterByTag(movies, tag), [movies, tag]);
  return filtered;
};

/**
 * Hook to get all movies without filtering
 * @returns {Array} Complete array of movies
 */
export const useAllMovies = () => {
  const moviesData = useSelector(state => state.Mode?.dataSource?.movies);
  
  return useMemo(() => {
    if (Array.isArray(moviesData) && moviesData.length > 0) {
      return moviesData;
    }
    // Log when using fallback data
    console.log('Using fallback data from db.json');
    return data.movies || [];
  }, [moviesData]);
};

// Export default categories for backward compatibility
export const categories = {
  homeLanderRow: filterByTag(data.movies, "home lander row"), // Add the home lander row
  test: filterByTag(data.movies, "test"),
  burrowPicks: filterByTag(data.movies, "burrow picks"),
  burrowUpdates: filterByTag(data.movies, "burrow updates"),
  disclosureHubFilms: filterByTag(data.movies, "disclosure hub films"),
  secretCivics: filterByTag(data.movies, "secret civics"),
  occultKattClips: filterByTag(data.movies, "occult katt clips"),
  daringDoctors: filterByTag(data.movies, "daring doctors"),
  injectionJustice: filterByTag(data.movies, "injection justice"),
  higherRealities: filterByTag(data.movies, "higher realities"),
  microwaveTechnology: filterByTag(data.movies, "microwave technology"),
  medicalMythologyAndNanoTech: filterByTag(data.movies, "medical mythology and nano tech"),
  healthWealth: filterByTag(data.movies, "health wealth"),
  godGivenRightsLaw: filterByTag(data.movies, "god given rights law"),
  godGivenRightsVLaw: filterByTag(data.movies, "god given rights v law"),
  bigTechTakeOver: filterByTag(data.movies, "big tech take over"),
  publicDisplays: filterByTag(data.movies, "public displays"),
  relationshipWars: filterByTag(data.movies, "relationship wars"),
  cloningAndGeneticEngineering: filterByTag(data.movies, "cloning and genetic engineering"),
  cryptidsAndUFOs: filterByTag(data.movies, "cryptids and ufos"),
  generalConspiracy: filterByTag(data.movies, "general conspiracy"),
  freeEnergyPotential: filterByTag(data.movies, "free energy potential"),
  falseIdolsAndTrauma: filterByTag(data.movies, "false idols and trauma"),
  darkEliteOccult: filterByTag(data.movies, "dark elite occult"),
  agenda2030WEFNWOAndFEMA: filterByTag(data.movies, "agenda 2030 wef nwo and fema"),
  the911Archive: filterByTag(data.movies, "the 911 archive"),
  qAndTeamNonBiasArchive: filterByTag(data.movies, "q and team non bias archive"),
  weatherModification: filterByTag(data.movies, "weather modification"),
  zombiesDatabase: filterByTag(data.movies, "zombies database"),
  // documentarySpotlight: filterByTag(data.movies, "documentary spotlight") - removed
};

// Create a React hook for loading mode-aware data
export const useMovieData = () => {
  const [movieData, setMovieData] = useState({ movies: [] });
  const dispatch = useDispatch();
  const manifestPath = useSelector(state => state.Mode?.manifestPath);
  const currentManifest = useSelector(state => state.Mode?.currentManifest);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log(`Loading data from manifest in: ${manifestPath}`);
        
        // Get list of available manifests
        const manifestsResult = await FileSystemService.getManifests();
        
        if (manifestsResult.success && manifestsResult.manifests?.length > 0) {
          // Filter for burrow-stream-manifest files and exclude temp files
          const validManifests = manifestsResult.manifests.filter(name => 
            name.startsWith('burrow-stream-manifest-') && 
            !name.includes('temp') && 
            !name.includes('test')
          );
          
          if (validManifests.length > 0) {
            // Sort by the timestamp in the filename for accurate date sorting
            const sortedManifests = [...validManifests].sort((a, b) => {
              const dateA = a.match(/burrow-stream-manifest-(\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2})\.json/);
              const dateB = b.match(/burrow-stream-manifest-(\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2})\.json/);
              
              if (dateA && dateB) {
                return dateB[1].localeCompare(dateA[1]); // Most recent first
              }
              return 0;
            });
            
            // Use the first (most recent) manifest
            const latestManifest = sortedManifests[0];
            dispatch(setCurrentManifest(latestManifest));
            
            // Load the manifest content
            const manifestResult = await FileSystemService.getManifest(latestManifest);
            
            if (manifestResult.success && manifestResult.manifest?.movies) {
              setMovieData({ 
                movies: manifestResult.manifest.movies 
              });
              console.log(`Loaded ${manifestResult.manifest.movies.length} movies from manifest: ${latestManifest}`);
            } else {
              console.warn("Invalid manifest data, using default data");
              setError(manifestResult.error || "Invalid manifest data");
              setMovieData({ movies: data.movies || [] }); // Fallback to default data
            }
          } else {
            console.warn("No valid burrow-stream-manifest files found");
            setError("No valid manifest files found");
            setMovieData({ movies: data.movies || [] }); // Fallback to default data
          }
        } else {
          console.warn("No manifests found, using default data");
          setError(manifestsResult.error || "No manifests found");
          setMovieData({ movies: data.movies || [] }); // Fallback to default data
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError(err.message);
        setMovieData({ movies: data.movies || [] }); // Fallback to default data
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [manifestPath, dispatch]);
  
  return { 
    movies: movieData.movies || [], 
    loading, 
    error,
    filterByTag: (tag) => filterByTag(movieData.movies || [], tag)
  };
};